import { useState, useEffect } from 'react';
import { Modal, ModalFooter } from './Modal';
import { Input } from './FormField';
import { Button } from '../ui/Button';
import { ErrorDisplay } from '../ui/ErrorDisplay';
import { fiscalYearApi } from '../../services/api';

type Template = 'quarterly' | 'monthly' | 'custom';

const MONTH_LABELS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

interface FiscalYear {
  id: string;
  year: string;
  label: string;
  quarters?: Array<{
    id: string;
    key: string;
    label: string;
    fullLabel: string;
  }>;
}

interface TimeframeModalProps {
  isOpen: boolean;
  onClose: () => void;
  fiscalYear?: FiscalYear | null;
  onSuccess: () => void;
}

function generatePreviewLabels(prefix: string, template: Template, customLabels: string[]): string[] {
  const p = prefix.trim() || '...';
  if (template === 'quarterly') {
    return ['Q1', 'Q2', 'Q3', 'Q4'].map(q => `${p} ${q}`);
  }
  if (template === 'monthly') {
    return MONTH_LABELS.map(m => `${p} ${m}`);
  }
  return customLabels.filter(l => l.trim()).map(l => `${p} ${l.trim()}`);
}

export function TimeframeModal({
  isOpen,
  onClose,
  fiscalYear,
  onSuccess,
}: TimeframeModalProps) {
  const [prefix, setPrefix] = useState(new Date().getFullYear().toString());
  const [template, setTemplate] = useState<Template>('quarterly');
  const [customLabels, setCustomLabels] = useState<string[]>(['', '']);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<Error | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isEditMode = Boolean(fiscalYear);

  useEffect(() => {
    if (fiscalYear) {
      setPrefix(fiscalYear.label || fiscalYear.year || '');
    } else {
      setPrefix(new Date().getFullYear().toString());
      setTemplate('quarterly');
      setCustomLabels(['', '']);
    }
    setErrors({});
    setSubmitError(null);
  }, [fiscalYear, isOpen]);

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!prefix.trim()) {
      newErrors.prefix = 'Column prefix is required';
    }

    if (template === 'custom') {
      const nonEmpty = customLabels.filter(l => l.trim());
      if (nonEmpty.length < 2) {
        newErrors.custom = 'At least 2 periods are required';
      }
      const seen = new Set<string>();
      for (const label of nonEmpty) {
        const trimmed = label.trim().toLowerCase();
        if (seen.has(trimmed)) {
          newErrors.custom = 'Period labels must be unique';
          break;
        }
        seen.add(trimmed);
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) return;

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const data: any = {
        prefix: prefix.trim(),
        template,
      };

      if (template === 'custom') {
        data.customPeriods = customLabels
          .filter(l => l.trim())
          .map(l => ({ label: l.trim() }));
      }

      if (isEditMode && fiscalYear) {
        onClose();
      } else {
        await fiscalYearApi.create(data);
      }

      onSuccess();
      onClose();
    } catch (error) {
      console.error('Failed to save timeframe:', error);
      setSubmitError(error instanceof Error ? error : new Error('Failed to save timeframe'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!fiscalYear) return;

    const periodCount = fiscalYear.quarters?.length || 0;
    if (!confirm(`Are you sure you want to delete "${fiscalYear.label}"? This will also remove ${periodCount} period(s).`)) {
      return;
    }

    setIsSubmitting(true);

    try {
      await fiscalYearApi.delete(fiscalYear.id);
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Failed to delete timeframe:', error);
      setSubmitError(error instanceof Error ? error : new Error('Failed to delete timeframe'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddCustomPeriod = () => {
    if (customLabels.length < 52) {
      setCustomLabels([...customLabels, '']);
    }
  };

  const handleRemoveCustomPeriod = (index: number) => {
    if (customLabels.length > 2) {
      setCustomLabels(customLabels.filter((_, i) => i !== index));
    }
  };

  const handleCustomLabelChange = (index: number, value: string) => {
    const updated = [...customLabels];
    updated[index] = value;
    setCustomLabels(updated);
  };

  const previewLabels = generatePreviewLabels(prefix, template, customLabels);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditMode ? 'View Timeframe' : 'Add Timeframe'}
      size="medium"
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Column Prefix Input */}
        <Input
          label="Column Prefix"
          name="prefix"
          type="text"
          value={prefix}
          onChange={(e) => setPrefix(e.target.value)}
          placeholder="2026"
          required
          error={errors.prefix}
          helpText={!isEditMode ? 'This text will appear before each period label (e.g., "2026 Q1", "FY26 Jan")' : undefined}
          disabled={isEditMode}
        />

        {/* Template Selector (create mode only) */}
        {!isEditMode && (
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Period Type
            </label>
            <div className="grid grid-cols-3 gap-2">
              {([
                { value: 'quarterly' as Template, label: 'Quarterly', desc: '4 periods (Q1-Q4)' },
                { value: 'monthly' as Template, label: 'Monthly', desc: '12 periods (Jan-Dec)' },
                { value: 'custom' as Template, label: 'Custom', desc: 'Define your own' },
              ]).map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setTemplate(opt.value)}
                  className={`p-3 rounded-lg border-2 text-left transition-colors ${
                    template === opt.value
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-border-default hover:border-gray-300'
                  }`}
                >
                  <div className={`text-sm font-medium ${
                    template === opt.value ? 'text-blue-700' : 'text-text-primary'
                  }`}>
                    {opt.label}
                  </div>
                  <div className="text-xs text-text-secondary mt-0.5">
                    {opt.desc}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Custom Period Labels (create + custom only) */}
        {!isEditMode && template === 'custom' && (
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Period Labels
            </label>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {customLabels.map((label, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <span className="text-xs text-text-secondary w-6 text-right">
                    {idx + 1}.
                  </span>
                  <input
                    type="text"
                    value={label}
                    onChange={(e) => handleCustomLabelChange(idx, e.target.value)}
                    placeholder={`Period ${idx + 1} (e.g., Sprint ${idx + 1})`}
                    className="flex-1 px-3 py-1.5 text-sm border border-border-default rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  {customLabels.length > 2 && (
                    <button
                      type="button"
                      onClick={() => handleRemoveCustomPeriod(idx)}
                      className="text-red-400 hover:text-red-600 text-sm px-1"
                    >
                      x
                    </button>
                  )}
                </div>
              ))}
            </div>
            {customLabels.length < 52 && (
              <button
                type="button"
                onClick={handleAddCustomPeriod}
                className="mt-2 text-sm text-blue-600 hover:text-blue-700"
              >
                + Add period
              </button>
            )}
            {errors.custom && (
              <p className="mt-1 text-sm text-red-600">{errors.custom}</p>
            )}
          </div>
        )}

        {/* Edit Mode: Show existing periods */}
        {isEditMode && fiscalYear?.quarters && (
          <div className="rounded-md bg-surface-subtle border border-border-subtle p-3">
            <div className="text-sm font-medium text-text-primary mb-2">
              Periods ({fiscalYear.quarters.length})
            </div>
            <div className="flex flex-wrap gap-2">
              {fiscalYear.quarters.map((q) => (
                <span
                  key={q.id}
                  className="px-2 py-1 bg-background-primary rounded text-xs text-text-secondary"
                >
                  {q.fullLabel}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Preview (create mode only) */}
        {!isEditMode && previewLabels.length > 0 && (
          <div className="rounded-md bg-blue-50 border border-blue-200 p-4 space-y-3">
            <div className="text-sm font-medium text-blue-800">Preview</div>

            {/* Simulated dropdown */}
            <div>
              <div className="text-xs text-blue-700 mb-1">
                Initiative Start / End dropdown will show:
              </div>
              <div className="bg-white rounded-md border border-blue-200 p-2 max-h-32 overflow-y-auto">
                {previewLabels.map((label, idx) => (
                  <div
                    key={idx}
                    className={`px-2 py-1 text-sm text-gray-700 rounded ${
                      idx === 0 ? 'bg-blue-100' : ''
                    }`}
                  >
                    {label}
                  </div>
                ))}
              </div>
            </div>

            {/* Timeline columns */}
            <div>
              <div className="text-xs text-blue-700 mb-1">
                Timeline columns:
              </div>
              <div className="flex gap-1 overflow-x-auto pb-1">
                {previewLabels.map((label, idx) => (
                  <div
                    key={idx}
                    className="flex-shrink-0 px-2 py-1 bg-white rounded border border-blue-200 text-xs text-gray-600 text-center"
                    style={{ minWidth: '60px' }}
                  >
                    {label}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        <ErrorDisplay error={submitError} />

        <ModalFooter>
          {isEditMode && (
            <Button
              type="button"
              variant="danger"
              onClick={handleDelete}
              disabled={isSubmitting}
              className="mr-auto"
            >
              Delete
            </Button>
          )}
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={isSubmitting}
          >
            {isEditMode ? 'Close' : 'Cancel'}
          </Button>
          {!isEditMode && (
            <Button type="submit" variant="primary" disabled={isSubmitting}>
              {isSubmitting ? 'Creating...' : 'Add Timeframe'}
            </Button>
          )}
        </ModalFooter>
      </form>
    </Modal>
  );
}
